import { useState } from "react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Upload, FileSpreadsheet, CheckCircle2 } from "lucide-react";

export default function BulkUpload() {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const downloadTemplate = async () => {
    const res = await api.get("/bulk/template", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = "digiloans_customer_template.xlsx"; a.click();
    URL.revokeObjectURL(url);
  };

  const upload = async () => {
    if (!file) return toast.error("Choose an .xlsx file first");
    setLoading(true); setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/bulk/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`${data.inserted} customers imported`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageHeader title="Bulk Upload" subtitle="Import many customers at once from an Excel sheet." />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-4xl">
        <Card className="p-6">
          <div className="w-12 h-12 rounded-xl bg-[#EBF3FA] text-[#0F4C81] flex items-center justify-center mb-4"><FileSpreadsheet size={24} /></div>
          <h3 className="font-bold text-slate-900 text-lg">1. Download Template</h3>
          <p className="text-sm text-slate-500 mt-1 mb-4">Get the standard .xlsx with all required columns and a sample row. Admins can add an <code>rm_username</code> per row to assign leads.</p>
          <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn"><Download size={16} className="mr-2" />Download Template</Button>
        </Card>

        <Card className="p-6">
          <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-4"><Upload size={24} /></div>
          <h3 className="font-bold text-slate-900 text-lg">2. Upload & Import</h3>
          <p className="text-sm text-slate-500 mt-1 mb-4">Duplicates are skipped automatically by mobile number.</p>
          <input type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files[0])} data-testid="upload-file-input"
            className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#0F4C81] file:text-white file:font-semibold hover:file:bg-[#0A3A66] mb-4" />
          <Button onClick={upload} disabled={loading} data-testid="upload-submit-btn" className="bg-emerald-600 hover:bg-emerald-700">{loading ? "Importing…" : "Import Customers"}</Button>
        </Card>
      </div>

      {result && (
        <Card className="p-6 mt-6 max-w-4xl" data-testid="upload-result">
          <div className="flex items-center gap-2 text-emerald-600 font-semibold mb-3"><CheckCircle2 size={18} />Import complete</div>
          <div className="flex gap-8 text-sm">
            <div><span className="text-2xl font-bold font-mono text-slate-900">{result.inserted}</span><div className="text-slate-500">Imported</div></div>
            <div><span className="text-2xl font-bold font-mono text-amber-600">{result.skipped_duplicates}</span><div className="text-slate-500">Duplicates skipped</div></div>
            <div><span className="text-2xl font-bold font-mono text-rose-600">{result.errors.length}</span><div className="text-slate-500">Errors</div></div>
          </div>
          {result.errors.length > 0 && <ul className="mt-4 text-xs text-rose-600 space-y-1">{result.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>}
        </Card>
      )}
    </div>
  );
}
